import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useCurrent, useFetch, usePost, usePut } from '../hooks'
import { AlertMsg, CardHeaderWithActions, MwButton, MwInputText, MwMultiSelector, MwSelector, MwSpinnerButton, MwTextArea, MwFormSection } from '../components/ui'
import { MdInfoOutline, MdPeopleOutline, MdSubject } from 'react-icons/md'
import { FORM_INPUT_CLASS, FORM_LABEL_CLASS } from '../constants/formStyles'
import ProjectAttachmentsField from '../components/projects/ProjectAttachmentsField'
import LangSwitch, { withLangLabel } from '../components/ui/LangSwitch'
import { arabicValueForEdit, validateBilingual } from '../utils/localized'
import { buildAttachmentNameMap, toAttachmentNamesPayload } from '../utils/attachments'
import { getAllowedProducts, getLocalizedOptions, normalizeSupportSettings } from '../constants/ticket'
import { buildAssigneesPayload, toAssigneeOptions } from '../utils/ticketAssignees'
import {
    buildTenantCustomerFields,
    getScopedTenants,
    getTenantLabel,
    isTruthyFlag,
    PROJECT_STATUSES,
    toDateInputValue,
    toIsoFromDateInput
} from '../utils/projectHelpers'

// إنشاء مشروع جديد، أو تعديل مشروع موجود لو فيه :id في الرابط.

const hasRichText = (html) => String(html || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0

const ProjectForm = () => {
    const navigate = useNavigate();
    const params = useParams();
    const projectId = params.id || null;
    const isEdit = Boolean(projectId);
    const [t] = useTranslation('global');
    const { user, currentLangId } = useCurrent();
    const isAr = currentLangId === 'ar';

    const canAssign =
        user?.role === 'admin' ||
        (isTruthyFlag(user?.supportAdminEnabled) &&
            (isTruthyFlag(user?.supportCanAssignTickets) || isTruthyFlag(user?.supportSuperAdminEnabled)));
    const canUseInternalProducts =
        user?.role === 'admin' ||
        (isTruthyFlag(user?.supportAdminEnabled) &&
            (isTruthyFlag(user?.supportCanViewInternalTickets) || isTruthyFlag(user?.supportSuperAdminEnabled)));

    const { data: supportSettingsResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/settings`);
    const { data: tenantsResult, loading: loadingTenants } = useFetch(`${process.env.REACT_APP_Auth_API_BASE_URL}/tenants/getAll`);
    const { data: adminsResult } = useFetch(canAssign ? `${process.env.REACT_APP_Auth_API_BASE_URL}/users/support-admins` : null);
    const { data: projectResult, loading: loadingProject } = useFetch(isEdit ? `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/projects/admin/${projectId}` : null);
    const { postData, loading: posting } = usePost();
    const { putData, loading: putting } = usePut();

    const [tenantSelected, setTenantSelected] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    // النسخة الإنجليزية + اللغة الظاهرة (زرار واحد بيبدّل الاسم والوصف مع بعض).
    const [titleEn, setTitleEn] = useState('');
    const [descriptionEn, setDescriptionEn] = useState('');
    const [formLang, setFormLang] = useState(currentLangId === 'en' ? 'en' : 'ar');
    const [assigneesSelected, setAssigneesSelected] = useState([]);
    const [sectionSelected, setSectionSelected] = useState(null);
    const [productSelected, setProductSelected] = useState(null);
    const [startAt, setStartAt] = useState('');
    const [expectedAt, setExpectedAt] = useState('');
    const [contractNumber, setContractNumber] = useState('');
    const statusOptions = useMemo(
        () => PROJECT_STATUSES.map((item) => ({ label: isAr ? item.ar : item.en, value: item.value, lang: currentLangId })),
        [currentLangId, isAr]
    );
    const [statusSelected, setStatusSelected] = useState(null);
    const effectiveStatus = statusSelected || statusOptions[0];
    const [attachments, setAttachments] = useState([]);
    const [attachmentNameMap, setAttachmentNameMap] = useState({});
    const [attachmentsBusy, setAttachmentsBusy] = useState(false);
    const [errors, setErrors] = useState({});
    const [hydrated, setHydrated] = useState(!isEdit);

    const supportSettings = useMemo(() => normalizeSupportSettings(supportSettingsResult), [supportSettingsResult]);
    const tenants = useMemo(() => getScopedTenants(tenantsResult?.data, user), [tenantsResult, user]);
    const tenantOptions = useMemo(
        () => tenants.map((tenant) => ({ label: getTenantLabel(tenant), value: tenant._id, lang: currentLangId })),
        [currentLangId, tenants]
    );
    const sectionOptions = useMemo(() => [
        { label: isAr ? 'بدون قسم' : 'No section', value: '', lang: currentLangId },
        ...getLocalizedOptions(supportSettings.sections, currentLangId)
    ], [currentLangId, isAr, supportSettings.sections]);
    const productOptions = useMemo(() => [
        { label: isAr ? 'بدون منتج' : 'No product', value: '', lang: currentLangId },
        ...getLocalizedOptions(
            getAllowedProducts(supportSettings.products, user?.supportProductIds, {
                fallbackToAll: user?.role === 'admin' || (isTruthyFlag(user?.supportAdminEnabled) && isTruthyFlag(user?.supportSuperAdminEnabled)),
                includeInternal: canUseInternalProducts,
                internalProductIds: user?.supportInternalProductIds,
            }),
            currentLangId
        )
    ], [canUseInternalProducts, currentLangId, isAr, supportSettings.products, user]);
    const adminOptions = useMemo(() => (Array.isArray(adminsResult?.data) ? adminsResult.data : []).map((admin) => ({
        label: admin?.name || admin?.username || admin?._id,
        value: admin?._id,
        lang: currentLangId,
        email: admin?.email || '',
        subLabel: admin?.jobTitle || '',
        avatar: admin?.userPic || admin?.avatar || admin?.image || admin?.profileImage || admin?.photo || ''
    })), [adminsResult, currentLangId]);

    const project = projectResult?.data || null;

    // تعبئة النموذج مرة واحدة بعد تحميل المشروع والإعدادات.
    useEffect(() => {
        if (!isEdit || hydrated || !project || !supportSettingsResult) return;
        setTenantSelected(
            tenantOptions.find((opt) => String(opt.value) === String(project.tenantId)) ||
            { label: project.tenantName || project.tenantId, value: project.tenantId, lang: currentLangId }
        );
        setTitle(arabicValueForEdit(project, 'title'));
        setDescription(arabicValueForEdit(project, 'description'));
        setTitleEn(project.titleEn || '');
        setDescriptionEn(project.descriptionEn || '');
        setAssigneesSelected(toAssigneeOptions(project, adminOptions, currentLangId));
        setSectionSelected(sectionOptions.find((opt) => opt.value === (project.sectionId || '')) || sectionOptions[0]);
        setProductSelected(productOptions.find((opt) => opt.value === (project.ProductId || '')) || productOptions[0]);
        setStartAt(toDateInputValue(project.startAt));
        setExpectedAt(toDateInputValue(project.expectedAt));
        setContractNumber(project.contractNumber || '');
        setStatusSelected(statusOptions.find((opt) => opt.value === (project.status || 'running')) || statusOptions[0]);
        setAttachments(Array.isArray(project.attachments) ? project.attachments : []);
        setAttachmentNameMap(buildAttachmentNameMap(project.attachmentNames));
        setHydrated(true);
    }, [adminOptions, currentLangId, hydrated, isEdit, productOptions, project, sectionOptions, statusOptions, supportSettingsResult, tenantOptions]);

    const saving = posting || putting;

    const submitHandler = async () => {
        const nextErrors = {};
        if (!tenantSelected?.value) nextErrors.tenant = isAr ? 'اختيار العميل مطلوب' : 'Customer is required';
        // الاسم مطلوب في أي لغة اتكتب فيها حاجة (الوصف اختياري).
        const bilingual = validateBilingual([
            { key: 'title', label: isAr ? 'عنوان المشروع' : 'Project title', required: true, ar: title, en: titleEn },
            { key: 'description', label: isAr ? 'وصف المشروع' : 'Project description', required: false, ar: description, en: descriptionEn },
        ], { isAr, preferredLang: formLang });
        if (!bilingual.valid) {
            nextErrors.bilingual = bilingual;
            setFormLang(bilingual.firstLang);
        }
        const startAtIso = toIsoFromDateInput(startAt);
        const expectedAtIso = toIsoFromDateInput(expectedAt, true);
        if (startAtIso && expectedAtIso && new Date(expectedAtIso) < new Date(startAtIso)) {
            nextErrors.expectedAt = isAr ? 'تاريخ النهاية لازم يكون بعد تاريخ البداية' : 'The end date must be after the start date';
        }
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length) return;

        const tenantRecord = tenants.find((tenant) => String(tenant?._id) === String(tenantSelected.value));
        const tenantChanged = !isEdit || String(tenantSelected.value) !== String(project?.tenantId || '');
        const payload = {
            title: title.trim() || titleEn.trim(),
            description,
            titleEn: titleEn.trim(),
            descriptionEn,
            ...(tenantChanged ? buildTenantCustomerFields(tenantRecord) : {}),
            sectionId: sectionSelected?.value || null,
            ProductId: productSelected?.value || null,
            startAt: startAtIso,
            expectedAt: expectedAtIso,
            status: effectiveStatus?.value || 'running',
            contractNumber: contractNumber.trim() || null,
            attachments,
            attachmentNames: toAttachmentNamesPayload(attachmentNameMap, attachments),
            ...(canAssign ? buildAssigneesPayload(assigneesSelected) : (!isEdit && user?._id ? buildAssigneesPayload([{ value: user._id, label: user?.name || '', email: user?.email || '' }]) : {}))
        };

        try {
            const result = isEdit
                ? await putData(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/projects/admin/${projectId}`, payload)
                : await postData(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/projects/admin`, payload);
            if (result?.status !== 1) throw new Error(result?.message);
            toast.success(isEdit ? (isAr ? 'تم تعديل المشروع' : 'Project updated') : (isAr ? 'تم إنشاء المشروع' : 'Project created'));
            navigate(`/${params.tenant}/projects/show/${result?.data?._id || projectId}`);
        } catch (err) {
            setErrors({ submit: err?.response?.data?.message || err?.message || (isAr ? 'فشل حفظ المشروع' : 'Failed to save project') });
        }
    };

    if (isEdit && (loadingProject || !hydrated)) {
        return (
            <div className="admin-page">
                <div className="admin-panel p-10 flex justify-center">
                    {projectResult && !project
                        ? <AlertMsg type="Error">{isAr ? 'المشروع غير موجود' : 'Project not found'}</AlertMsg>
                        : <MwSpinnerButton withLabel={false} isFullCenter />}
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <CardHeaderWithActions title={isEdit ? (isAr ? `تعديل المشروع #${project?.projectNumber ?? ''}` : `Edit Project #${project?.projectNumber ?? ''}`) : (isAr ? 'إنشاء مشروع' : 'Create Project')}>
                <MwButton type="cancelBtn" withIcon={false} onClick={() => navigate(isEdit ? `/${params.tenant}/projects/show/${projectId}` : `/${params.tenant}/projects`)}>
                    {isAr ? 'إلغاء' : 'Cancel'}
                </MwButton>
                {saving ? <MwSpinnerButton /> : (
                    <MwButton type="saveBtn" onClick={submitHandler} disabled={attachmentsBusy}>
                        {isEdit ? (isAr ? 'حفظ التعديل' : 'Save') : (isAr ? 'إنشاء المشروع' : 'Create Project')}
                    </MwButton>
                )}
            </CardHeaderWithActions>
            <div className="admin-panel p-3 md:p-5 flex flex-col gap-4">
                {errors.submit && <AlertMsg type="Error">{errors.submit}</AlertMsg>}
                <MwFormSection
                    title={isAr ? 'بيانات المشروع' : 'Project details'}
                    icon={<MdInfoOutline size={15} />}
                >
                    <MwInputText
                        label={isAr ? 'رقم المشروع' : 'Project #'}
                        labelClassName={FORM_LABEL_CLASS}
                        classNameInput={FORM_INPUT_CLASS}
                        value={isEdit ? String(project?.projectNumber ?? '') : ''}
                        placeholder={isAr ? 'يتم توليده تلقائياً عند الحفظ' : 'Generated automatically on save'}
                        readOnly
                        disabled
                    />
                    <div className="sm:col-span-2 xl:col-span-3 flex flex-col gap-1">
                        <LangSwitch
                            value={formLang}
                            onChange={setFormLang}
                            isAr={isAr}
                            filled={{ ar: Boolean(title.trim() || hasRichText(description)), en: Boolean(titleEn.trim() || hasRichText(descriptionEn)) }}
                            errors={errors.bilingual?.errors}
                            messages={errors.bilingual?.messages}
                        />
                        <MwInputText
                            label={withLangLabel(isAr ? 'عنوان المشروع' : 'Project title', formLang, isAr)}
                            labelClassName={FORM_LABEL_CLASS}
                            classNameInput={FORM_INPUT_CLASS}
                            value={formLang === 'ar' ? title : titleEn}
                            onChange={(e) => (formLang === 'ar' ? setTitle : setTitleEn)(e.target.value)}
                            invalid={!!errors.bilingual?.errors?.[formLang]?.title}
                            invalidMsg={errors.bilingual?.errors?.[formLang]?.title}
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <MwSelector
                            className="z-[60]"
                            label={isAr ? 'العميل' : 'Customer'}
                            initalValue={isAr ? 'اختر عميل' : 'Select customer'}
                            dataType="labelValue"
                            _data={tenantOptions}
                            selectedItem={tenantSelected}
                            setSelectedItem={setTenantSelected}
                            withAddNew={false}
                            invalid={!!errors.tenant}
                            invalidMsg={errors.tenant}
                        />
                    </div>
                    <MwInputText
                        label={isAr ? 'رقم العقد (اختياري)' : 'Contract number (optional)'}
                        labelClassName={FORM_LABEL_CLASS}
                        classNameInput={FORM_INPUT_CLASS}
                        value={contractNumber}
                        onChange={(e) => setContractNumber(e.target.value)}
                        placeholder={isAr ? 'مثال: 20242131' : 'e.g. 20242131'}
                    />
                    <MwSelector
                        className="z-[51]"
                        label={isAr ? 'حالة المشروع' : 'Project status'}
                        initalValue={isAr ? 'اختر الحالة' : 'Select status'}
                        dataType="labelValue"
                        _data={statusOptions}
                        selectedItem={effectiveStatus}
                        setSelectedItem={setStatusSelected}
                        withAddNew={false}
                    />
                    <MwSelector
                        className="z-50"
                        label={t('support.tickets.product')}
                        initalValue={t('support.tickets.selectProductPlaceholder')}
                        dataType="labelValue"
                        _data={productOptions}
                        selectedItem={productSelected}
                        setSelectedItem={setProductSelected}
                        withAddNew={false}
                    />
                    <MwSelector
                        className="z-[49]"
                        label={t('support.tickets.section')}
                        initalValue={t('support.tickets.selectSectionPlaceholder')}
                        dataType="labelValue"
                        _data={sectionOptions}
                        selectedItem={sectionSelected}
                        setSelectedItem={setSectionSelected}
                        withAddNew={false}
                    />
                </MwFormSection>

                <MwFormSection
                    title={isAr ? 'المواعيد والتعيين' : 'Dates & assignment'}
                    icon={<MdPeopleOutline size={15} />}
                >
                    <MwInputText
                        label={isAr ? 'تاريخ البداية' : 'Start date'}
                        labelClassName={FORM_LABEL_CLASS}
                        classNameInput={FORM_INPUT_CLASS}
                        inputType="date"
                        value={startAt}
                        onChange={(e) => setStartAt(e.target.value)}
                    />
                    <MwInputText
                        label={isAr ? 'تاريخ النهاية المتوقع' : 'Expected end date'}
                        labelClassName={FORM_LABEL_CLASS}
                        classNameInput={FORM_INPUT_CLASS}
                        inputType="date"
                        value={expectedAt}
                        onChange={(e) => setExpectedAt(e.target.value)}
                        invalid={!!errors.expectedAt}
                        invalidMsg={errors.expectedAt}
                    />
                    {canAssign && (
                        <div className="sm:col-span-2">
                            <MwMultiSelector
                                className="z-[40]"
                                label={isAr ? 'معينة لـ' : 'Assigned To'}
                                initalValue={isAr ? 'غير معين — اختر موظف أو أكثر...' : 'Unassigned — select one or more admins...'}
                                _data={adminOptions}
                                selectedItems={assigneesSelected}
                                setSelectedItems={setAssigneesSelected}
                            />
                        </div>
                    )}
                </MwFormSection>

                <MwFormSection
                    title={isAr ? 'وصف المشروع والمرفقات' : 'Description & attachments'}
                    icon={<MdSubject size={15} />}
                    cols={1}
                >
                    <LangSwitch
                        value={formLang}
                        onChange={setFormLang}
                        isAr={isAr}
                        filled={{ ar: Boolean(title.trim() || hasRichText(description)), en: Boolean(titleEn.trim() || hasRichText(descriptionEn)) }}
                        errors={errors.bilingual?.errors}
                    />
                    {['ar', 'en'].map((code) => (
                        <div key={code} className={formLang === code ? '' : 'hidden'}>
                            <MwTextArea
                                label={withLangLabel(isAr ? 'وصف المشروع' : 'Project description', code, isAr)}
                                minHeight={480}
                                value={code === 'ar' ? description : descriptionEn}
                                onChange={(e) => (code === 'ar' ? setDescription : setDescriptionEn)(e.target.value)}
                                dir={code === 'ar' ? 'rtl' : 'ltr'}
                                rich
                            />
                        </div>
                    ))}
                    <ProjectAttachmentsField
                        value={attachments}
                        names={attachmentNameMap}
                        onChange={setAttachments}
                        onNamesChange={(uploaded) => setAttachmentNameMap((current) => ({ ...current, ...uploaded }))}
                        onBusyChange={setAttachmentsBusy}
                        lang={currentLangId}
                    />
                </MwFormSection>
                {loadingTenants && (
                    <p className="text-xs text-slate-500">{isAr ? 'جاري تحميل العملاء...' : 'Loading customers...'}</p>
                )}
            </div>
        </div>
    );
};

export default ProjectForm;
